import { useState } from 'react';
import { T, useTheme, dens, Ico, Card, Eyebrow, Btn, Badge, Field, ChoiceChips } from '../../../design';
import type { IconName } from '../../../design';
import { normalizePhone, fmtPhone, fmtBirthday, fmtMemberSince } from '../../../utils';
import { MAX_FAMILY, MEMBER_STATUS } from '../primeConfig';
import { PhoneInput } from './PhoneInput';
import type { PrimeMember, PrimeFamilyMember } from '../../../types/prime.types';

export interface MemberDetailProps {
  member: PrimeMember;
  onAddFamily: (phone: string) => void;
  onRemoveFamily: (f: PrimeFamilyMember) => void;
  onCancel: () => void;
  onReactivate: (plan: 'monthly' | 'yearly') => void;
  onSync: () => void;
  busy: boolean;
  syncing: boolean;
}

function initials(name: string | null): string {
  return (name || '?').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function SrStatus() {
  return <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>Status: </span>;
}

function InfoCell({ label, value, full, icon }: { label: string; value: string | null; full?: boolean; icon: IconName }) {
  const Ic = Ico[icon];
  return (
    <div style={{ padding: '13px 16px', gridColumn: full ? '1 / -1' : 'auto', borderTop: `1px solid ${T.borderLight}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Ic && <Ic size={12} style={{ color: T.fg3 }} />}
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.fg3 }}>{label}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
}

export function MemberDetail({ member, onAddFamily, onRemoveFamily, onCancel, onReactivate, onSync, busy, syncing }: MemberDetailProps) {
  const t = useTheme();
  const d = dens(t);
  const [famPhone, setFamPhone] = useState('');
  const [plan, setPlan] = useState<'monthly' | 'yearly'>('monthly');
  const st = MEMBER_STATUS[member.status] || MEMBER_STATUS.active;
  const isPrimary = member.type === 'primary';
  const fam = member.family || [];
  const slotsLeft = MAX_FAMILY - fam.length;
  const normFam = normalizePhone(famPhone);
  const validFam = normFam.length === 8;
  const dup = validFam && (normFam === member.phone || fam.some((f) => f.phone === normFam));

  function submitFamily() {
    if (!validFam || dup || busy) return;
    onAddFamily(famPhone);
    setFamPhone('');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
      {/* Kundekort */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 13, alignItems: 'center', padding: d.pad }}>
          <span style={{ width: 52, height: 52, borderRadius: 15, background: member.status === 'deactivated' ? T.track : T.accentLight, color: member.status === 'deactivated' ? T.fg3 : t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 17, flexShrink: 0 }}>{initials(member.name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.fg, letterSpacing: '-0.01em' }}>{member.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
              <Badge tone={st.tone} dot><SrStatus />{st.label}</Badge>
              <Badge tone="neutral">{isPrimary ? 'Hovedmedlem' : 'Familiemedlem'}</Badge>
            </div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${T.borderLight}`, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <InfoCell label="Email" value={member.email} full icon="receipt" />
          <InfoCell label="Telefon" value={fmtPhone(member.phone)} icon="phone" />
          <InfoCell label="Fødselsdag" value={fmtBirthday(member.birthday)} icon="star" />
          {member.memberSince && <InfoCell label="Medlem siden" value={fmtMemberSince(member.memberSince)} icon="clock" />}
          {!isPrimary && member.parentName && <InfoCell label="Tilhører" value={`${member.parentName} · ${fmtPhone(member.parentPhone || '')}`} full icon="users" />}
        </div>
      </Card>

      {member.status === 'deactivated' && (
        <Card pad={14} style={{ background: T.errorBg, border: `1px solid ${T.errorBorder}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Ico.info size={17} style={{ color: T.error, flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12.5, color: T.errorFg, fontWeight: 600, lineHeight: 1.5 }}>Medlemskabet er deaktiveret. Vælg abonnement og genaktivér for at give kunden adgang igen.</div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Field label="Abonnement">
              <ChoiceChips
                value={plan}
                onChange={(v) => setPlan(v as 'monthly' | 'yearly')}
                options={[{ value: 'monthly', label: 'Månedlig' }, { value: 'yearly', label: 'Årlig' }]}
              />
            </Field>
          </div>
          <Btn full size="md" style={{ marginTop: 12 }} disabled={busy} onClick={() => onReactivate(plan)} icon={<Ico.checkCircle size={18} />}>Genaktivér medlemskab</Btn>
        </Card>
      )}

      {/* Familiemedlemmer — kun for hovedmedlemmer */}
      {isPrimary && member.status !== 'deactivated' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, padding: '4px 2px 0' }}>
            <Eyebrow>Familiemedlemmer</Eyebrow>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: slotsLeft > 0 ? T.fg3 : T.error }}>{fam.length} / {MAX_FAMILY} pladser</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
            {fam.length === 0 && (
              <Card pad={16} style={{ textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: T.accentLight, color: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9px' }}><Ico.users size={20} /></div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.fg }}>Ingen familiemedlemmer endnu</div>
                <div style={{ fontSize: 12, color: T.fg3, fontWeight: 600, marginTop: 3 }}>Inviter op til {MAX_FAMILY} gratis husstandsmedlemmer.</div>
              </Card>
            )}
            {fam.map((f) => {
              const fst = MEMBER_STATUS[f.status] || MEMBER_STATUS.active;
              return (
                <Card key={f.id ?? f.phone} pad={0}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 13px' }}>
                    <span style={{ width: 40, height: 40, borderRadius: 12, background: f.status === 'invited' ? T.warningBg : T.accentLight, color: f.status === 'invited' ? T.warningFg : t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{initials(f.name)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name ?? fmtPhone(f.phone)}</div>
                      <div style={{ fontSize: 12, color: T.fg3, fontWeight: 600, marginTop: 2 }}>{fmtPhone(f.phone)}</div>
                    </div>
                    <Badge tone={fst.tone} dot style={{ flexShrink: 0 }}><SrStatus />{f.status === 'invited' ? 'Inviteret' : 'Aktiv'}</Badge>
                    <button onClick={() => onRemoveFamily(f)} aria-label={`Fjern ${f.name ?? fmtPhone(f.phone)}`} style={{ border: 0, background: 'transparent', color: T.fg3, width: 34, height: 34, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><Ico.trash size={17} /></button>
                  </div>
                </Card>
              );
            })}

            {slotsLeft > 0 ? (
              <Card pad={d.pad}>
                <Field label="Inviter familiemedlem" hint={`${slotsLeft} ${slotsLeft === 1 ? 'plads' : 'pladser'} tilbage`} error={dup ? 'Nummeret er allerede tilknyttet.' : null}>
                  <PhoneInput value={famPhone} onChange={setFamPhone} ariaLabel="Familiemedlems mobilnummer" placeholder="Familiemedlems mobilnummer" onEnter={submitFamily} />
                </Field>
                <Btn full size="md" variant="soft" style={{ marginTop: 12 }} disabled={!validFam || dup || busy} onClick={submitFamily} icon={<Ico.plus size={18} />}>Send invitation</Btn>
              </Card>
            ) : (
              <Card pad={14} style={{ background: T.bg }}>
                <div style={{ fontSize: 12.5, color: T.fg2, fontWeight: 600, lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'center' }}><Ico.info size={16} style={{ color: T.fg3, flexShrink: 0 }} />Alle {MAX_FAMILY} familiepladser er i brug. Fjern et medlem for at invitere et nyt.</div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Handlinger */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 4 }}>
        <Btn full size="md" variant="secondary" disabled={syncing} onClick={onSync} icon={<Ico.sync size={18} />}>{syncing ? 'Opdaterer…' : 'Opdater bookingsystem'}</Btn>
        {member.status !== 'deactivated' && <Btn full size="md" variant="danger" onClick={onCancel} icon={<Ico.x size={18} />}>Annullér medlemskab</Btn>}
      </div>
    </div>
  );
}
